/**
 * Block 14 «Раскрой» — чистая математика pinch-zoom таблицы Деталей (PR #8b,
 * ADR-0071, реализует ADR-0069 D4). Без DOM/React — полностью юнит-тестируемо.
 *
 * Модель таблицы (только мобильный ≤480px): freeze-header (нативный
 * `position:sticky` thead) + нативный 2D-скролл тела (`.sc-table-wrap`
 * `overflow:auto`) + равномерный size-zoom через CSS-переменную `--cell-scale`
 * (font-size/padding ячеек масштабируются, столбцы остаются выровнены — одна
 * таблица). Pan/tap — нативные (1 палец = скролл/тап); pinch (2 пальца) меняет
 * `--cell-scale` (см. usePinchZoom).
 *
 * Здесь — только примитивы pinch: масштаб из отношения дистанций между пальцами,
 * зажатый в границы. Pan/clamp офсета не нужны (нативный скролл сам ограничивает).
 */

export interface Point {
  x: number;
  y: number;
}

/** Границы масштаба (решение владельца STOP-pre PR #8b). */
export const MIN_SCALE = 0.5;
export const MAX_SCALE = 3;

/** Зажать масштаб в [MIN_SCALE, MAX_SCALE]. */
export function clampScale(scale: number): number {
  if (Number.isNaN(scale)) return 1;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** Евклидова дистанция между двумя точками (расстояние между пальцами для pinch). */
export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/* ====================================================================
   SVG-карта раскроя (ADR-0088, B-246/CUT-5.2b) — геометрический зум/пан.
   Отдельно от табличного size-zoom выше: здесь transform на внутреннем
   `<g>` карты, состояние вида {scale, tx, ty}. Чистая математика без DOM.
   ==================================================================== */

/** Границы масштаба карты (ADR-0088): 1× = «вписано», 4× — потолок. */
export const MAP_MIN_SCALE = 1;
export const MAP_MAX_SCALE = 4;

/** Зажать масштаб карты в [MAP_MIN_SCALE, MAP_MAX_SCALE]. */
export function clampMapScale(scale: number): number {
  if (Number.isNaN(scale)) return 1;
  return Math.min(MAP_MAX_SCALE, Math.max(MAP_MIN_SCALE, scale));
}

/** Состояние вида карты: `transform="translate(tx ty) scale(scale)"`. */
export interface ViewTransform {
  scale: number;
  tx: number;
  ty: number;
}

/** Тождественный вид (без зума/пана). */
export const IDENTITY_VIEW: ViewTransform = { scale: 1, tx: 0, ty: 0 };

/** Середина отрезка между пальцами (центр щипка). */
export function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Масштабирование вокруг точки `anchor` (в координатах родителя `<g>` =
 * пространство viewBox): точка под anchor остаётся на месте. `factor` —
 * множитель к текущему масштабу; итоговый scale зажат в [1,4], смещение
 * пересчитано по фактически применённому множителю (после clamp).
 */
export function zoomAround(
  v: ViewTransform,
  factor: number,
  anchorX: number,
  anchorY: number,
): ViewTransform {
  const scale = clampMapScale(v.scale * factor);
  const applied = scale / v.scale; // фактический множитель после clamp
  return {
    scale,
    tx: anchorX - applied * (anchorX - v.tx),
    ty: anchorY - applied * (anchorY - v.ty),
  };
}

/** Сдвиг вида на (dx, dy) в координатах viewBox (пан). */
export function panBy(v: ViewTransform, dx: number, dy: number): ViewTransform {
  return { scale: v.scale, tx: v.tx + dx, ty: v.ty + dy };
}

/** SVG transform-строка из состояния вида. */
export function toTransformString(v: ViewTransform): string {
  return `translate(${v.tx} ${v.ty}) scale(${v.scale})`;
}
